import { useEffect, useState } from 'react';

const THEME_STORAGE_KEY = 'theme';

const readStoredTheme = () => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const getInitialTheme = () => {
  const stored = readStoredTheme();
  if (stored === 'dark' || stored === 'light') return stored;

  const documentTheme = document.documentElement.getAttribute('data-theme');
  if (documentTheme === 'dark' || documentTheme === 'light') return documentTheme;

  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export default function useTheme() {
  const [theme, setTheme] = useState(getInitialTheme);

  // Follow the system preference until the user makes an explicit choice.
  useEffect(() => {
    if (readStoredTheme() || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      const next = event.matches ? 'dark' : 'light';
      setTheme(next);
      document.documentElement.setAttribute('data-theme', next);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
    return undefined;
  }, []);

  // Keep the DOM attribute in sync with state.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) themeColor.setAttribute('content', theme === 'dark' ? '#1c1d1b' : '#faf9f6');
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch (error) {
        /* ignore (private mode) */
      }
      return next;
    });
  };

  return { theme, toggleTheme };
}
